import { useEffect, useState } from 'react'
import api from '../../services/api'

/**
 * Which email categories this person wants.
 *
 * It replaces two switches that only ever moved React state — no request, no column, nothing.
 * Somebody could turn "Email Notifications" off, keep receiving email, and reasonably conclude
 * the app lies to them. This one drops their address before anything is sent.
 *
 * The point is not tidiness. Months of staging test mail taught everybody that a ProTrack email
 * is probably nothing, and a real deliverable was ignored along with the tests. Mail that never
 * arrives cannot train anyone to ignore the next one.
 *
 * Two environments, two honest promises, and the screen says which one it is making:
 *   staging     — untick anything, it stops arriving, all of it
 *   production   — untick to silence the news; a request someone is waiting on still arrives
 *
 * That second promise is the entire reason this is safe to ship. A switch that could hide the
 * email that was missed would rebuild the incident, and worse — the person would believe they
 * had chosen it.
 */
export default function EmailCategoryPrefs({ isDark }) {
  const [state, setState] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    let gone = false
    api.get('/users/me/notification-prefs')
      .then(({ data }) => { if (!gone) setState(data) })
      .catch(() => { if (!gone) setError('Could not load your email settings.') })
    return () => { gone = true }
  }, [])

  // Saved on change rather than behind a Save button: there is nothing to review, and a button
  // people forget to press is how a preference screen ends up lying about what it holds.
  const toggle = async (key) => {
    const next = state.categories.map(c => c.key === key ? { ...c, enabled: !c.enabled } : c)
    setState({ ...state, categories: next })   // move immediately; the switch must feel real
    setError(null)
    setSaved(false)
    setSaving(true)
    try {
      const body = Object.fromEntries(next.map(c => [c.key, c.enabled]))
      const { data } = await api.put('/users/me/notification-prefs', { categories: body })
      setState(data)                            // trust the server's version, not ours
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch {
      // Put it back. A switch that stayed where you left it while the save failed is exactly
      // the dishonesty this replaces.
      setState({ ...state })
      setError('That did not save — check your connection and try again.')
    } finally {
      setSaving(false)
    }
  }

  const textPrimary = isDark ? 'text-white' : 'text-gray-900'
  const textSecondary = isDark ? 'text-gray-400' : 'text-gray-500'

  if (error && !state) {
    return <p className="text-sm text-red-600 py-4">{error}</p>
  }
  if (!state) {
    return <p className={`text-sm py-4 ${textSecondary}`}>Loading your email settings…</p>
  }

  return (
    <div className="py-2">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div>
          <p className={`font-medium ${textPrimary}`}>Which emails you get</p>
          <p className={`text-sm ${textSecondary}`}>
            {state.everything_optional
              ? `Untick anything you don't want. This is ${state.environment} — nothing here is real, so nothing you switch off matters.`
              : 'Untick anything you don’t want. Requests someone is waiting on still arrive — you cannot switch those off, and neither can anyone else.'}
          </p>
        </div>
        <span className={`text-xs shrink-0 pt-1 ${saved ? 'text-green-600' : textSecondary}`}>
          {saving ? 'Saving…' : saved ? 'Saved' : ''}
        </span>
      </div>

      {error && <p className="text-xs text-red-600 mt-1 mb-1">{error}</p>}

      <div className={`mt-3 divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-100'}`}>
        {state.categories.map(c => (
          <label
            key={c.key}
            className="flex items-start gap-3 py-3 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={c.enabled}
              onChange={() => toggle(c.key)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
            />
            <span className="min-w-0">
              <span className={`block text-sm font-medium ${textPrimary}`}>{c.label}</span>
              <span className={`block text-xs ${textSecondary}`}>{c.description}</span>
              {/* Named, not hidden. Somebody who unticks this and still gets three emails from
                  it should have been told that would happen, on this screen, beforehand. */}
              {c.note && (
                <span className="block text-xs text-amber-600 dark:text-amber-500 mt-0.5">
                  {c.note}
                </span>
              )}
            </span>
          </label>
        ))}
      </div>
    </div>
  )
}
